import { useDispatch, useSelector } from "react-redux";
import { useEffect, useMemo, useRef, useState } from "react";
import { auctionPropertiesThunks } from "../../app/store";
import {
  Button,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Select,
  Spinner,
  Textarea,
} from "flowbite-react";
import { useNavigate, useParams } from "react-router-dom";
import { HiSparkles } from "react-icons/hi";
import { encode } from "@toon-format/toon";
import { aiApi } from "../../api/aiApi";

const RATINGS = ["A", "B", "C", "REDEEMED"];
const AI_INPUT_FORMAT = process.env.REACT_APP_AI_INPUT_FORMAT || "toon";
const SYSTEM_PROMPT = `You are a real estate investment analyst specialized in tax lien and tax deed properties in the United States.

Your role is to analyze a property and determine:
- How easy it is to exit (sell, rent, finance, flip)
- Risks and hidden issues
- Overall investment attractiveness

You must think like an experienced investor focused on PROFIT and LIQUIDITY.

---

## 🎯 OBJECTIVE

Evaluate the property and assign a grade based on EXIT POTENTIAL:

A = Very easy to profit (high demand, easy resale, multiple exit strategies)
B = Good deal but with minor limitations
C = Difficult to sell or monetize
D = Problematic property with serious risks or low liquidity

The grade is based primarily on:
- Ease of selling
- Ease of renting
- Ease of financing (seller finance potential)
- Flip potential
- Market demand

---

## 🔍 ANALYSIS RULES

### 1. Red Flags (VERY IMPORTANT)
Identify any serious issues such as:
- Landlocked property (no legal access)
- Bad or unclear legal description
- Property in undesirable or declining area
- Flood zones, wetlands, unusable land
- Extremely rural with no demand
- Title complications or unclear ownership risks
- HOA or restrictions that limit usage
- Very small, odd-shaped, or unusable lots
- Environmental or zoning issues

---

### 2. Green Flags
Identify positive signals such as:
- Located in growing or desirable area
- Close to cities, roads, or infrastructure
- Utilities available or nearby
- Residential zoning with build potential
- Comparable sales nearby
- Demand for housing or rentals
- Easy access (road frontage)

---

### 3. Legal Description Analysis
Carefully evaluate the legal description:
- Check if it suggests access issues (e.g., metes and bounds without road reference)
- Look for easements or lack of access clues
- Identify potential complexity or ambiguity

If anything is unclear or risky → flag it.

---

### 4. Market Insight
Provide insights about the area when possible:
- Demand level (high / medium / low)
- Type of buyers likely interested
- Liquidity (fast vs slow market)
- Strategy suggestions (flip, hold, finance, avoid)

---

### 5. Exit Strategy Evaluation
Analyze viability of:
- Selling as-is
- Seller financing
- Renting
- Building
- Flipping

---

## 📦 OUTPUT FORMAT (JSON ONLY)

Return ONLY valid JSON with this structure:

{
  "grade": "A | B | C | D",
  "summary": "Short explanation of the overall evaluation",
  "red_flags": [
    "list of risks"
  ],
  "green_flags": [
    "list of positives"
  ],
  "insights": [
    "market or strategy insights"
  ],
  "legal_analysis": {
    "risk_level": "low | medium | high",
    "notes": "analysis of legal description"
  },
  "exit_strategies": {
    "sell": "easy | moderate | difficult",
    "rent": "easy | moderate | difficult",
    "seller_finance": "easy | moderate | difficult",
    "build": "easy | moderate | difficult",
    "flip": "easy | moderate | difficult"
  }
}

---

## ⚠️ IMPORTANT RULES

- Be skeptical and conservative (assume risk when unclear)
- If something is unknown, mention it as a risk
- Do NOT hallucinate specific data — infer cautiously
- Focus on INVESTMENT DECISION, not general description
- Avoid unnecessary explanations outside JSON
`;

function buildPropertyPayload(property) {
  if (!property) return null;
  const safeFields = [
    "id",
    "parcel_number",
    "owner_name",
    "property_address",
    "city",
    "county",
    "state_code",
    "zip_code",
    "latitude",
    "longitude",
    "legal_description",
    "min_bid",
    "appraisal_value",
    "land_value",
    "improvement_value",
    "lot_area",
    "type_lot",
    "homestead_investor",
    "zoning_code",
    "contexto",
    "relevo",
    "fema_zone_code",
    "rating",
    "zillow_url",
    "electric",
    "water",
    "sewer",
  ];

  const payload = {};
  safeFields.forEach((field) => {
    if (property[field] !== undefined) payload[field] = property[field];
  });

  const snapshotSource =
    property?.regridSnapshots ||
    property?.PropertyRegridSnapshots ||
    property?.regridSnapshot ||
    [];
  if (snapshotSource.length) {
    payload.regridSnapshots = snapshotSource.slice(0, 2).map((snap) => ({
      parcel_number: snap.parcel_number,
      property_address: snap.property_address,
      city: snap.city,
      county: snap.county,
      state_code: snap.state_code,
      zip_code: snap.zip_code,
      regrid_score: snap.regrid_score,
      owner_name: snap.owner_name,
      sale_price: snap.sale_price,
      appraisal_value: snap.appraisal_value,
    }));
  }

  const lienSource = property?.liens || property?.PropertyLiens || [];
  if (lienSource.length) {
    payload.liensSummary = {
      total: lienSource.length,
      openOrUnknown: lienSource.filter(
        (l) => !l.status || l.status === "OPEN" || l.status === "UNKNOWN",
      ).length,
    };
  }

  return payload;
}

function buildAiState(property) {
  return {
    auctionProperties: {
      current: buildPropertyPayload(property) || {},
    },
  };
}

function extractAiResult(aiResult) {
  if (!aiResult?.candidates?.length) return null;
  const text = aiResult.candidates[0]?.content?.parts?.map((p) => p.text).join("");
  if (!text) return null;
  const cleaned = text.replace(/```json/gi, "").replace(/```/gi, "").trim();
  try {
    return JSON.parse(cleaned);
  } catch {
    return null;
  }
}

function parseStoredJson(value) {
  if (!value) return null;
  if (typeof value === "object") return value;
  try {
    return JSON.parse(value);
  } catch {
    return null;
  }
}

function restoreStoredAiResult(evaluation) {
  if (!evaluation) return null;

  const rawResponse = parseStoredJson(evaluation.raw_response);
  if (rawResponse?.candidates?.length) return rawResponse;

  const parsedJson = parseStoredJson(evaluation.parsed_json);
  if (!parsedJson) return null;

  return {
    modelVersion: "avaliação salva",
    candidates: [
      {
        content: {
          parts: [{ text: JSON.stringify(parsedJson) }],
        },
      },
    ],
  };
}

export default function VaReview({ propertyId, property, onSave }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { auctionId } = useParams();

  const currentStateProperty = useSelector(
    (state) => state.auctionProperties?.current || null,
  );
  const [saving, setSaving] = useState(false);
  const [aiLoading, setAiLoading] = useState(false);
  const [aiResult, setAiResult] = useState(null);
  const [aiError, setAiError] = useState(null);
  const [toonModalOpen, setToonModalOpen] = useState(false);
  const [toonPreview, setToonPreview] = useState("");
  const [toonCopyStatus, setToonCopyStatus] = useState("");
  const toonTextareaRef = useRef(null);
  const parsedAiResult = useMemo(() => extractAiResult(aiResult), [aiResult]);
  const aiFallbackText = useMemo(() => {
    if (!aiResult?.candidates?.length) return null;
    return aiResult.candidates[0]?.content?.parts?.map((p) => p.text).join("") || null;
  }, [aiResult]);
  const lastEvaluation = useMemo(
    () => property?.aiEvaluations?.[0] || null,
    [property],
  );

  const initialForm = useMemo(
    () => ({
      rating: property?.rating || "",
      va_comments: property?.va_comments || "",
    }),
    [property?.rating, property?.va_comments],
  );

  const [form, setForm] = useState(initialForm);

  useEffect(() => {
    setForm(initialForm);
  }, [propertyId, initialForm]);

  useEffect(() => {
    setAiResult(restoreStoredAiResult(lastEvaluation));
    setAiError(null);
  }, [propertyId, lastEvaluation]);

  async function save() {
    if (!propertyId) return;
    setSaving(true);

    try {
      await dispatch(
        auctionPropertiesThunks.updateOne({
          id: propertyId,
          payload: {
            rating: form.rating || null,
            va_comments: form.va_comments?.trim() || null,
          },
        }),
      ).unwrap();

      onSave?.();

      if (auctionId) {
        navigate(`/app/auctions/${auctionId}/properties`);
      } else {
        navigate("../..", { replace: true });
      }
    } catch (err) {
      console.error("Falha ao salvar o review:", err);
    } finally {
      setSaving(false);
    }
  }

  async function runAiEvaluation() {
    setAiLoading(true);
    setAiError(null);
    try {
      const state = buildAiState(currentStateProperty || property);
      const result = await aiApi.evaluate({
        systemPrompt: SYSTEM_PROMPT,
        state,
        propertyId,
        inputFormat: AI_INPUT_FORMAT,
      });
      setAiResult(result);
      if (propertyId) {
        dispatch(auctionPropertiesThunks.getById(propertyId));
      }
    } catch (error) {
      setAiResult(null);
      setAiError(error?.message || "Falha ao executar a IA");
    } finally {
      console.log('currentStateProperty            :', currentStateProperty);
      setAiLoading(false);
    }
  }

  function openToonPreview() {
    setToonPreview(encode(buildAiState(currentStateProperty || property)));
    setToonCopyStatus("");
    setToonModalOpen(true);
  }

  async function copyToonPreview() {
    try {
      await navigator.clipboard.writeText(toonPreview);
      setToonCopyStatus("Copiado");
    } catch {
      toonTextareaRef.current?.select();
      const copied = document.execCommand("copy");
      setToonCopyStatus(copied ? "Copiado" : "Não foi possível copiar");
    }
  }

  return (
    <div className="p-4 space-y-4">
      <div className="rounded-lg border border-gray-200 dark:border-gray-800 p-4 bg-white dark:bg-gray-900 space-y-4">
        <div className="text-sm font-medium text-gray-900 dark:text-white">
          VA Review
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label>Rating</Label>
            <Select
              value={form.rating}
              onChange={(e) =>
                setForm((s) => ({ ...s, rating: e.target.value }))
              }
            >
              <option value="">— Selecione —</option>
              {RATINGS.map((v) => (
                <option key={v} value={v}>
                  {v}
                </option>
              ))}
            </Select>

            <div className="text-xs text-gray-500 mt-1">
              A = Excelente | B = Avaliar | C = Nao recomendado | REDEEMED = Resgatada
            </div>
          </div>

          <div className="md:col-span-2">
            <Label>VA Comments</Label>
            <Textarea
              rows={5}
              value={form.va_comments}
              onChange={(e) =>
                setForm((s) => ({ ...s, va_comments: e.target.value }))
              }
              placeholder="Resumo final da análise, riscos, pontos positivos, observações importantes para decisão..."
            />
          </div>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-2 pt-2">
          <div className="flex flex-col gap-1">
            <div className="flex items-center gap-2">
              <Button
                color="info"
                onClick={runAiEvaluation}
                disabled={aiLoading}
                className="flex items-center gap-2"
              >
                {aiLoading ? (
                  <>
                    <Spinner size="sm" />
                    Pensando...
                  </>
                ) : (
                  <>
                    <HiSparkles className="h-4 w-4" />
                    Pensar com IA
                  </>
                )}
              </Button>
              <Button color="gray" size="xs" onClick={openToonPreview}>
                Ver TOON
              </Button>
              {aiError && (
                <div className="text-xs text-red-600">{aiError}</div>
              )}
            </div>
            {lastEvaluation && (
              <div className="text-[10px] text-gray-500">
                Última avaliação: {lastEvaluation.grade || "—"} em{" "}
                {lastEvaluation.created_at
                  ? new Date(lastEvaluation.created_at).toLocaleDateString("pt-BR")
                  : "—"}
              </div>
            )}
          </div>
          <Button color="gray" onClick={save} disabled={saving}>
            {saving ? "Salvando..." : "Salvar Review"}
          </Button>
        </div>
      </div>

      {aiResult && (
        <div className="space-y-4">
          <div className="rounded-lg border border-blue-200 bg-blue-50 p-4 text-xs text-gray-900">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-sm font-semibold text-blue-900">
                  Saída da IA
                </div>
                <div className="text-[10px] text-blue-700">
                  Modelo: {aiResult.modelVersion || "gemini"} · tokens{" "}
                  {aiResult.usageMetadata?.totalTokenCount || "—"}
                  {aiResult.inputFormat
                    ? ` · entrada ${aiResult.inputFormat.toUpperCase()}`
                    : ""}
                </div>
              </div>
              <div className="text-[10px] text-gray-600">
                {parsedAiResult ? "JSON válido gerado" : "JSON bruto"}
              </div>
            </div>

            {parsedAiResult ? (
              <div className="mt-4 space-y-4 text-[11px] text-gray-900">
                <div className="text-[13px] font-semibold">
                  Grade {parsedAiResult.grade || "—"} ·{" "}
                  <span className="font-normal text-gray-600">
                    {parsedAiResult.summary}
                  </span>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                  <div>
                    <div className="text-[11px] font-semibold text-gray-700">
                      Red Flags
                    </div>
                    <ul className="list-disc list-inside text-gray-800 space-y-1">
                      {parsedAiResult.red_flags?.length ? (
                        parsedAiResult.red_flags.map((flag, index) => (
                          <li key={`red-${index}`}>{flag}</li>
                        ))
                      ) : (
                        <li className="text-gray-500">Nenhuma encontrada</li>
                      )}
                    </ul>
                  </div>
                  <div>
                    <div className="text-[11px] font-semibold text-gray-700">
                      Green Flags
                    </div>
                    <ul className="list-disc list-inside text-gray-800 space-y-1">
                      {parsedAiResult.green_flags?.length ? (
                        parsedAiResult.green_flags.map((flag, index) => (
                          <li key={`green-${index}`}>{flag}</li>
                        ))
                      ) : (
                        <li className="text-gray-500">Nenhuma encontrada</li>
                      )}
                    </ul>
                  </div>
                </div>

                <div>
                  <div className="text-[11px] font-semibold text-gray-700">
                    Insights
                  </div>
                  <ul className="list-disc list-inside text-gray-800 space-y-1">
                    {parsedAiResult.insights?.length ? (
                      parsedAiResult.insights.map((insight, index) => (
                        <li key={`insight-${index}`}>{insight}</li>
                      ))
                    ) : (
                      <li className="text-gray-500">Sem insights</li>
                    )}
                  </ul>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="rounded border border-gray-200 bg-white p-3 text-[11px]">
                    <div className="font-semibold text-gray-700">
                      Legal Analysis
                    </div>
                    <div className="text-xs text-gray-500">
                      Risk level:{" "}
                      {parsedAiResult.legal_analysis?.risk_level || "—"}
                    </div>
                    <p className="mt-1 text-gray-800">
                      {parsedAiResult.legal_analysis?.notes ||
                        "Nenhuma nota disponível"}
                    </p>
                  </div>
                  <div className="rounded border border-gray-200 bg-white p-3 text-[11px] space-y-2">
                    <div className="font-semibold text-gray-700">
                      Exit Strategies
                    </div>
                    {parsedAiResult.exit_strategies ? (
                      <div className="grid grid-cols-2 gap-2 text-gray-800">
                        {Object.entries(parsedAiResult.exit_strategies).map(
                          ([key, value]) => (
                            <div
                              key={key}
                              className="flex justify-between text-[10px]"
                            >
                              <span className="capitalize">
                                {key.replace("_", " ")}
                              </span>
                              <span className="font-semibold">{value}</span>
                            </div>
                          ),
                        )}
                      </div>
                    ) : (
                      <div className="text-gray-500">
                        Estratégias não avaliadas
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ) : (
              <pre className="mt-3 max-h-64 overflow-auto whitespace-pre-wrap">
                {aiFallbackText}
              </pre>
            )}
          </div>
        </div>
      )}

      <Modal
        show={toonModalOpen}
        onClose={() => setToonModalOpen(false)}
        size="4xl"
      >
        <ModalHeader>Dados enviados à IA em TOON</ModalHeader>
        <ModalBody>
          <div className="space-y-2">
            <p className="text-xs text-gray-500 dark:text-gray-400">
              Este é o mesmo contexto da propriedade enviado ao Gemini.
            </p>
            <Textarea
              ref={toonTextareaRef}
              value={toonPreview}
              readOnly
              rows={22}
              className="font-mono text-xs"
            />
          </div>
        </ModalBody>
        <ModalFooter>
          <Button color="info" onClick={copyToonPreview}>
            Copiar TOON
          </Button>
          <Button color="gray" onClick={() => setToonModalOpen(false)}>
            Fechar
          </Button>
          {toonCopyStatus && (
            <span className="text-xs text-gray-500">{toonCopyStatus}</span>
          )}
        </ModalFooter>
      </Modal>
    </div>
  );
}
